import { useEffect, useMemo, useState } from "react";
import { FiChevronDown, FiChevronUp, FiX, FiPlus } from "react-icons/fi";
import Fuse from "fuse.js";

import { formatUSPhone } from "src/Common/function";
import AddressInput from "src/Components/Common/addressinput";
import { SelectDropdown } from "./components/components";

const emptyLocation = () => ({
  key: Math.random().toString(36).slice(2),
  companyName: "",
  contact: "",
  email: "",
  phone: "",
  address: "",
  branch: null,
  collapsed: false,
});

// Fuse score: 0 = perfect match, 1 = no match. Anything at or below this is
// treated as "similar enough" to suggest. Roughly ~80% similarity.
const FUSE_SCORE_THRESHOLD = 0.4;

const fuseOptions = {
  includeScore: true,
  // Match anywhere in the string, not just near the start — handles partial
  // matches and shifted word positions ("Acme Corp" vs "The Acme Corp").
  ignoreLocation: true,
  threshold: FUSE_SCORE_THRESHOLD,
  keys: ["normalized"],
};

/**
 * Handles the flows shown in the mockups:
 *  - Create Customer: customer name + one or more inline locations.
 *  - Edit Customer: customer name only.
 *  - Add Location (`mode="add-location"`): pick an existing customer, then add
 *    one or more locations to it.
 */
function CustomerAddModal({
  mode = "customer",
  account,
  accounts = [],
  branches = [],
  onAddClicked,
  onUpdateClicked,
  onAddLocationClicked,
  onCreateLocationClicked,
  onCancelClicked,
  preselectedAccountId,
}) {
  const isEditMode = Boolean(account?.id);
  const isAddLocationMode = mode === "add-location";

  const [name, setName] = useState("");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [locations, setLocations] = useState([emptyLocation()]);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [duplicateError, setDuplicateError] = useState(false);

  // In add-location mode, preselect the customer when one is passed in.
  useEffect(() => {
    if (!isAddLocationMode || !preselectedAccountId) return;
    const match = accounts.find((item) => item.id === preselectedAccountId);
    if (match) setSelectedAccount(match);
  }, [isAddLocationMode, preselectedAccountId, accounts]);

  useEffect(() => {
    setName(account?.name || "");
  }, [account]);

  const existingNames = useMemo(
    () =>
      accounts
        .filter((item) => item.id !== account?.id)
        .map((item) => ({ original: (item.name || "").trim(), normalized: (item.name || "").trim().toLowerCase() }))
        .filter((item) => item.normalized),
    [accounts, account?.id],
  );

  const fuse = useMemo(() => new Fuse(existingNames, fuseOptions), [existingNames]);

  // Closest existing customer name when the typed name is similar (or identical).
  const similarName = useMemo(() => {
    const typed = name.trim().toLowerCase();
    if (!typed) return null;

    // An exact match is the best possible suggestion.
    const exact = existingNames.find((item) => item.normalized === typed);
    if (exact) return exact.original;

    const [best] = fuse.search(typed, { limit: 1 });
    return best ? best.item.original : null;
  }, [name, existingNames, fuse]);

  // Fixed "Customer Name -" prefix applied to every location name so
  // locations are always searchable by their customer. Live-tracks the typed
  // customer name (create flow) or the selected customer (add-location flow).
  const locationNamePrefix = (isAddLocationMode ? selectedAccount?.name || "" : name).trim();

  const withPrefix = (locationName) =>
    locationNamePrefix ? `${locationNamePrefix} - ${locationName.trim()}` : locationName.trim();

  const updateLocation = (key, patch) => {
    setLocations((previous) => previous.map((loc) => (loc.key === key ? { ...loc, ...patch } : loc)));
  };

  const addLocation = () => {
    setLocations((previous) => [...previous, emptyLocation()]);
  };

  const removeLocation = (key) => {
    setLocations((previous) => (previous.length === 1 ? previous : previous.filter((loc) => loc.key !== key)));
  };

  // A location requires every field except address. Branch is an object.
  const isLocationComplete = (loc) =>
    loc.companyName.trim() && loc.contact.trim() && loc.email.trim() && loc.phone.trim() && loc.branch;

  // Expand any collapsed location that is missing fields so its errors are visible.
  const expandIncompleteLocations = () => {
    setLocations((previous) => previous.map((loc) => (isLocationComplete(loc) ? loc : { ...loc, collapsed: false })));
  };

  const submit = () => {
    if (isSubmitting) return;
    setSubmitted(true);

    // Add-location flow: pick an existing customer, then add locations to it.
    if (isAddLocationMode) {
      if (!selectedAccount) return;

      // Every location must be fully filled (address aside).
      if (!locations.every(isLocationComplete)) {
        expandIncompleteLocations();
        return;
      }

      setIsSubmitting(true);
      onAddLocationClicked({
        accountId: selectedAccount.id,
        locations: locations.map((loc) => ({
          companyName: withPrefix(loc.companyName),
          contact: loc.contact.trim(),
          email: loc.email.trim(),
          phone: loc.phone.trim(),
          address: loc.address.trim() || undefined,
          branch: loc.branch,
        })),
      });
      return;
    }

    const trimmedName = name.trim();
    if (!trimmedName) return;

    if (existingNames.some((item) => item.normalized === trimmedName.toLowerCase())) {
      setDuplicateError(true);
      return;
    }

    setIsSubmitting(true);

    if (isEditMode) {
      onUpdateClicked({ id: account.id, name: trimmedName });
      return;
    }

    // Create flow: every location must be fully filled (address aside).
    if (!locations.every(isLocationComplete)) {
      setIsSubmitting(false);
      expandIncompleteLocations();
      return;
    }

    onAddClicked({
      name: trimmedName,
      locations: locations.map((loc) => ({
        companyName: withPrefix(loc.companyName),
        contact: loc.contact.trim(),
        email: loc.email.trim(),
        phone: loc.phone.trim(),
        address: loc.address.trim() || undefined,
        branchId: loc.branch.id,
      })),
    });
  };

  return (
    <div
      onClick={onCancelClicked}
      className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`${
          isEditMode ? "w-[500px]" : "w-[640px]"
        } max-w-full max-h-[90vh] bg-white rounded-[12px] shadow-[0px_25px_50px_-12px_rgba(0,0,0,0.25)] flex flex-col overflow-hidden`}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-[#E5E8F3]">
          <div className="flex-1">
            <h2 className="text-[20px] leading-[28px] font-OpenSansSemiBold text-[#1A2332] capitalize">
              {isAddLocationMode ? "New Location" : isEditMode ? "Edit Customer" : "New Customer"}
            </h2>
            <p className="mt-1 text-[14px] leading-[20px] tracking-[0.14px] font-OpenSansRegular text-[#7C829A]">
              {isAddLocationMode
                ? "Add a new service location to an existing customer account."
                : "Create a new customer and its first location."}
            </p>
          </div>
          <FiX size={20} className="text-[#868DA6] cursor-pointer" onClick={onCancelClicked} />
        </div>

        {/* Body */}
        <div className="px-8 py-8 overflow-y-auto flex-1 flex flex-col gap-8">
          {isAddLocationMode ? (
            /* Customer picker */
            <div className="flex flex-col gap-1">
              <SelectDropdown
                label="Customer"
                required
                searchable
                searchPlaceholder="Search customers…"
                placeholder="Select customer"
                value={selectedAccount}
                options={accounts}
                getLabel={(a) => a.name}
                getKey={(a) => a.id}
                onSelect={(a) => setSelectedAccount(a)}
                error={submitted && !selectedAccount}
              />
              <p className="text-[12px] leading-[18px] tracking-[0.12px] font-OpenSansRegular text-[#7C829A]">
                Locations belong to Customers. Select an existing Customer before adding a new Location.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {/* Customer name */}
              <label className="block text-[14px] leading-[20px] tracking-[0.14px] font-OpenSansSemiBold text-[#1A2332] capitalize">
                Customer Name <span className="text-[#EF4444]">*</span>
              </label>
              <input
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (duplicateError) setDuplicateError(false);
                }}
                placeholder="Enter customer name"
                className={`w-full h-[40px] px-3 rounded-[6px] border text-[14px] leading-[20px] tracking-[0.14px] font-OpenSansRegular text-[#1A2332] outline-none focus:border-blueColor ${
                  (submitted && !name.trim()) || duplicateError ? "border-red-500" : "border-[#E5E8F3]"
                }`}
              />
              {similarName ? (
                <p className="text-[12px] leading-[18px] tracking-[0.12px] text-[#B45309] font-OpenSansRegular">
                  Did you mean{" "}
                  <button
                    type="button"
                    onClick={() => {
                      setName(similarName);
                      if (duplicateError) setDuplicateError(false);
                    }}
                    className="font-OpenSansSemiBold text-[#B45309] underline"
                  >
                    {similarName}
                  </button>
                  ? A customer with this name already exists.{" "}
                  <span className="text-blueColor">To add a location to it,</span>{" "}
                  <button
                    type="button"
                    onClick={() => {
                      const match = accounts.find(
                        (item) => (item.name || "").trim().toLowerCase() === similarName.toLowerCase(),
                      );
                      onCreateLocationClicked?.(match?.id);
                    }}
                    className="font-OpenSansSemiBold text-blueColor underline"
                  >
                    click here
                  </button>
                  .
                </p>
              ) : (
                <p className="text-[12px] leading-[18px] tracking-[0.12px] text-[#868DA6] font-OpenSansRegular">
                  Customer account name used throughout the system.
                </p>
              )}
            </div>
          )}

          {/* Locations (create + add-location) */}
          {!isEditMode && (
            <div className="flex flex-col gap-4 pt-6 border-t border-borderColor">
              <div className="flex items-center gap-2 justify-between">
                <h3 className="text-[16px] leading-[24px] font-OpenSansSemiBold text-titleColor capitalize">
                  Locations
                </h3>
                <span className="text-[14px] leading-[20px] tracking-[0.14px] font-OpenSansRegular text-[#7C829A]">
                  At least one location required
                </span>
              </div>

              <div className="flex flex-col gap-4">
                {locations.map((loc, index) => (
                  <div key={loc.key} className="border border-borderColor rounded-[8px] overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 bg-backgroundColor border-b border-borderColor">
                      <button
                        type="button"
                        onClick={() => updateLocation(loc.key, { collapsed: !loc.collapsed })}
                        className="flex items-center gap-2 text-[16px] leading-[24px] font-OpenSansSemiBold text-titleColor capitalize"
                      >
                        {loc.collapsed ? <FiChevronDown size={18} /> : <FiChevronUp size={18} />}
                        Location {index + 1} <span className="text-red-500">*</span>
                      </button>
                      {locations.length > 1 && (
                        <FiX
                          size={18}
                          className="text-subTitleColor cursor-pointer"
                          onClick={() => removeLocation(loc.key)}
                        />
                      )}
                    </div>

                    {!loc.collapsed && (
                      <div className="px-4 py-4 flex flex-col gap-4">
                        <Field
                          label="Location Name"
                          required
                          prefix={locationNamePrefix}
                          value={loc.companyName}
                          onChange={(v) => updateLocation(loc.key, { companyName: v })}
                          placeholder="Enter location name"
                          error={submitted && !loc.companyName.trim()}
                        />

                        <div className="grid grid-cols-2 gap-4">
                          <Field
                            label="Contact Name"
                            required
                            value={loc.contact}
                            onChange={(v) => updateLocation(loc.key, { contact: v })}
                            placeholder="Enter contact name"
                            error={submitted && !loc.contact.trim()}
                          />
                          <Field
                            label="Email Address"
                            required
                            type="email"
                            value={loc.email}
                            onChange={(v) => updateLocation(loc.key, { email: v })}
                            placeholder="contact@example.com"
                            error={submitted && !loc.email.trim()}
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <Field
                            label="Phone Number"
                            required
                            value={loc.phone}
                            onChange={(v) => updateLocation(loc.key, { phone: formatUSPhone(v) })}
                            placeholder="(808) 555-0100"
                            error={submitted && !loc.phone.trim()}
                          />
                          <SelectDropdown
                            label="Branch"
                            required
                            placeholder="Select branch"
                            value={loc.branch}
                            options={branches}
                            getLabel={(b) => b.name}
                            getKey={(b) => b.id}
                            onSelect={(b) => updateLocation(loc.key, { branch: b })}
                            error={submitted && !loc.branch}
                          />
                        </div>

                        <div className="flex flex-col gap-1">
                          <label className="block text-[14px] leading-[20px] tracking-[0.14px] font-OpenSansSemiBold text-titleColor capitalize">
                            Address
                          </label>
                          <AddressInput
                            value={loc.address}
                            onChange={(v) => updateLocation(loc.key, { address: v })}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={addLocation}
                className="h-[40px] px-1 flex items-center gap-2 text-[16px] leading-[24px] font-OpenSansSemiBold text-blueColor capitalize"
              >
                <FiPlus size={16} /> Add Another Location
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-4 px-6 py-4 border-t border-[#E5E8F3]">
          <button
            type="button"
            onClick={onCancelClicked}
            className="h-[40px] px-1 rounded-[6px] text-[16px] leading-[24px] font-OpenSansSemiBold text-[#7C829A] capitalize"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={isSubmitting}
            className={`h-[40px] px-4 rounded-[8px] text-[16px] leading-[24px] font-OpenSansSemiBold bg-[#0065FD] text-white capitalize ${
              isSubmitting ? "opacity-60 cursor-not-allowed" : ""
            }`}
          >
            {isAddLocationMode
              ? `Add Location${locations.length === 1 ? "" : "s"}`
              : isEditMode
                ? "Save Changes"
                : "Create Customer"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, required, type = "text", error, prefix }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="block text-[14px] leading-[20px] tracking-[0.14px] font-OpenSansSemiBold text-titleColor capitalize">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {prefix ? (
        <div
          className={`w-full h-[40px] flex items-center rounded-[6px] border focus-within:border-blueColor ${
            error ? "border-red-500" : "border-borderColor"
          }`}
        >
          <span className="pl-3 max-w-[55%] truncate whitespace-nowrap select-none text-[14px] leading-[20px] tracking-[0.14px] font-OpenSansRegular text-[#868DA6]">
            {prefix} -
          </span>
          <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="flex-1 min-w-0 h-full pl-1 pr-3 rounded-[6px] outline-none text-[14px] leading-[20px] tracking-[0.14px] font-OpenSansRegular text-inputEmailTitle"
          />
        </div>
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`w-full h-[40px] px-3 rounded-[6px] border text-[14px] leading-[20px] tracking-[0.14px] font-OpenSansRegular text-inputEmailTitle outline-none focus:border-blueColor ${
            error ? "border-red-500" : "border-borderColor"
          }`}
        />
      )}
    </div>
  );
}

export default CustomerAddModal;
